import axios from 'axios';

// Configure axios defaults
// In production, use the same origin with /api prefix; in development, use localhost:5000
export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5000/api';
  }
  return `${window.location.origin}/api`;
};
axios.defaults.baseURL = getApiBaseUrl();

// Add token to requests if it exists
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token expiration
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await axios.post('/auth/login', { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  register: async (userData: any) => {
    const response = await axios.post('/auth/register', userData);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getCurrentUser: async () => {
    const response = await axios.get('/auth/me');
    return response.data.user;
  },

  getLinkedInAuthUrl: async () => {
    const response = await axios.get('/auth/linkedin');
    return response.data;
  },

  linkedInCallback: async (code: string, state: string) => {
    const response = await axios.post('/auth/linkedin/callback', { code, state });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  updatePassword: async (currentPassword: string, newPassword: string) => {
    const response = await axios.put('/auth/updatepassword', { currentPassword, newPassword });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },
};

// Projects API
export const projectsAPI = {
  getAll: async () => {
    const response = await axios.get('/projects');
    return response.data.projects;
  },

  getMyProjects: async () => {
    const response = await axios.get('/projects', { params: { assigned: 'true' } });
    return response.data.projects;
  },

  getById: async (id: string) => {
    const response = await axios.get(`/projects/${id}`);
    return response.data.project;
  },

  create: async (projectData: any) => {
    const response = await axios.post('/projects', projectData);
    return response.data.project;
  },

  createWithDocument: async (projectData: any, document?: File) => {
    const formData = new FormData();
    formData.append('projectData', JSON.stringify(projectData));
    if (document) {
      formData.append('document', document);
    }

    const response = await axios.post('/projects/with-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  createWithDocuments: async (projectData: any, documents?: File[]) => {
    const formData = new FormData();
    formData.append('projectData', JSON.stringify(projectData));
    if (documents && documents.length > 0) {
      documents.forEach((doc) => {
        formData.append('documents', doc);
      });
    }

    const response = await axios.post('/projects/with-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (id: string, projectData: any) => {
    const response = await axios.put(`/projects/${id}`, projectData);
    return response.data.project;
  },

  delete: async (id: string) => {
    await axios.delete(`/projects/${id}`);
  },

  getStats: async (id: string) => {
    const response = await axios.get(`/projects/${id}/stats`);
    return response.data.stats;
  },

  uploadHeaderImage: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append('headerImage', file);

    const response = await axios.post(`/projects/${id}/header-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteHeaderImage: async (id: string) => {
    const response = await axios.delete(`/projects/${id}/header-image`);
    return response.data;
  },

  uploadSectionImage: async (id: string, section: string, file: File) => {
    const formData = new FormData();
    formData.append('sectionImage', file);
    const response = await axios.post(`/projects/${id}/section-image/${section}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  reinitialize: async (id: string, documents: File[], options?: {
    clearExistingPersonas?: boolean;
    clearExistingStrategies?: boolean;
    clearExistingWorkPlannings?: boolean;
    clearExistingCoostoTags?: boolean;
    triggerCoostoSync?: boolean;
  }) => {
    const formData = new FormData();
    documents.forEach((doc) => {
      formData.append('documents', doc);
    });
    if (options) {
      formData.append('options', JSON.stringify(options));
    }

    const response = await axios.post(`/projects/${id}/reinitialize`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Document management
  getDocuments: async (id: string) => {
    const response = await axios.get(`/projects/${id}/documents`);
    return response.data.documents;
  },

  uploadDocuments: async (id: string, documents: File[]) => {
    const formData = new FormData();
    documents.forEach((doc) => {
      formData.append('documents', doc);
    });

    const response = await axios.post(`/projects/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteDocument: async (projectId: string, documentId: string) => {
    const response = await axios.delete(`/projects/${projectId}/documents/${documentId}`);
    return response.data;
  },

  analyzeDocuments: async (id: string, options: {
    documentIds?: string[];
    clearExistingPersonas?: boolean;
    clearExistingStrategies?: boolean;
    clearExistingWorkPlannings?: boolean;
    clearExistingCoostoTags?: boolean;
    triggerCoostoSync?: boolean;
  }) => {
    const response = await axios.post(`/projects/${id}/documents/analyze`, options);
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getOverview: async () => {
    const response = await axios.get('/dashboard/overview');
    return response.data.overview;
  },

  getProjectDashboard: async (projectId: string) => {
    const response = await axios.get(`/dashboard/project/${projectId}`);
    return response.data.dashboard;
  },
};

// Sentiment API
export const sentimentAPI = {
  getByProject: async (projectId: string, params?: any) => {
    const response = await axios.get(`/sentiment/project/${projectId}`, { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await axios.get(`/sentiment/${id}`);
    return response.data.sentiment;
  },

  create: async (sentimentData: any) => {
    const response = await axios.post('/sentiment', sentimentData);
    return response.data.sentiment;
  },

  update: async (id: string, sentimentData: any) => {
    const response = await axios.put(`/sentiment/${id}`, sentimentData);
    return response.data.sentiment;
  },

  delete: async (id: string) => {
    await axios.delete(`/sentiment/${id}`);
  },

  getAnalytics: async (projectId: string, params?: any) => {
    const response = await axios.get(`/sentiment/project/${projectId}/analytics`, { params });
    return response.data.analytics;
  },

  respond: async (id: string, responseData: any) => {
    const response = await axios.post(`/sentiment/${id}/respond`, responseData);
    return response.data.sentiment;
  },

  getEscalationCheck: async (projectId: string) => {
    const response = await axios.get(`/sentiment/project/${projectId}/escalation-check`);
    return response.data;
  },
};

// Personas API
export const personasAPI = {
  getByProject: async (projectId: string) => {
    const response = await axios.get(`/personas/project/${projectId}`);
    return response.data.personas;
  },

  getById: async (id: string) => {
    const response = await axios.get(`/personas/${id}`);
    return response.data.persona;
  },

  create: async (personaData: any) => {
    const response = await axios.post('/personas', personaData);
    return response.data.persona;
  },

  update: async (id: string, personaData: any) => {
    const response = await axios.put(`/personas/${id}`, personaData);
    return response.data.persona;
  },

  delete: async (id: string) => {
    await axios.delete(`/personas/${id}`);
  },

  duplicate: async (id: string) => {
    const response = await axios.post(`/personas/${id}/duplicate`);
    return response.data.persona;
  },

  fetchAvatars: async (projectId: string) => {
    const response = await axios.post(`/personas/project/${projectId}/fetch-avatars`);
    return response.data;
  },

  regenerateAvatars: async (projectId: string) => {
    const response = await axios.post(`/personas/project/${projectId}/regenerate-avatars`);
    return response.data;
  },
};

// Strategy API
export const strategyAPI = {
  getByProject: async (projectId: string, params?: any) => {
    const response = await axios.get(`/strategy/project/${projectId}`, { params });
    return response.data.strategies;
  },

  getById: async (id: string) => {
    const response = await axios.get(`/strategy/${id}`);
    return response.data.strategy;
  },

  create: async (strategyData: any) => {
    const response = await axios.post('/strategy', strategyData);
    return response.data.strategy;
  },

  update: async (id: string, strategyData: any) => {
    const response = await axios.put(`/strategy/${id}`, strategyData);
    return response.data.strategy;
  },

  delete: async (id: string) => {
    await axios.delete(`/strategy/${id}`);
  },

  approve: async (id: string, approvalData: any) => {
    const response = await axios.post(`/strategy/${id}/approve`, approvalData);
    return response.data.strategy;
  },
};

// Media API
export const mediaAPI = {
  getByProject: async (projectId: string, params?: any) => {
    const response = await axios.get(`/media/project/${projectId}`, { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await axios.get(`/media/${id}`);
    return response.data.media;
  },

  upload: async (formData: FormData) => {
    const response = await axios.post('/media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.media;
  },

  update: async (id: string, mediaData: any) => {
    const response = await axios.put(`/media/${id}`, mediaData);
    return response.data.media;
  },

  delete: async (id: string) => {
    await axios.delete(`/media/${id}`);
  },

  download: async (id: string) => {
    const response = await axios.get(`/media/${id}/download`);
    return response.data.downloadUrl;
  },
};

// Tasks API
export const tasksAPI = {
  getByProject: async (projectId: string, params?: any) => {
    const response = await axios.get(`/tasks/project/${projectId}`, { params });
    return response.data.tasks;
  },

  getById: async (id: string) => {
    const response = await axios.get(`/tasks/${id}`);
    return response.data.task;
  },

  create: async (taskData: any) => {
    const response = await axios.post('/tasks', taskData);
    return response.data.task;
  },

  update: async (id: string, taskData: any) => {
    const response = await axios.put(`/tasks/${id}`, taskData);
    return response.data.task;
  },

  delete: async (id: string) => {
    await axios.delete(`/tasks/${id}`);
  },

  complete: async (id: string) => {
    const response = await axios.post(`/tasks/${id}/complete`);
    return response.data.task;
  },

  backwardPlanning: async (projectId: string, data: { taskType: string; deadline: string; title: string }) => {
    const response = await axios.post(`/tasks/backward-planning`, { projectId, ...data });
    return response.data;
  },
};

// Work Planning API (legacy - kept for compatibility)
export const workPlanningAPI = {
  getByProject: async (projectId: string, params?: any) => {
    return tasksAPI.getByProject(projectId, params);
  },

  getMyTasks: async (params?: any) => {
    const response = await axios.get('/work-planning/my-tasks', { params });
    return response.data.tasks;
  },

  getById: async (id: string) => {
    return tasksAPI.getById(id);
  },

  create: async (taskData: any) => {
    return tasksAPI.create(taskData);
  },

  update: async (id: string, taskData: any) => {
    return tasksAPI.update(id, taskData);
  },

  delete: async (id: string) => {
    return tasksAPI.delete(id);
  },

  addComment: async (id: string, comment: string) => {
    const response = await axios.post(`/work-planning/${id}/comment`, { text: comment });
    return response.data.task;
  },

  getCalendar: async (projectId: string, month: number, year: number) => {
    const response = await axios.get(`/work-planning/project/${projectId}/calendar`, {
      params: { month, year },
    });
    return response.data.tasks;
  },
};

// Questions API
export const questionsAPI = {
  getByProject: async (projectId: string, params?: any) => {
    const response = await axios.get(`/questions/project/${projectId}`, { params });
    return response.data;
  },

  getPublic: async (projectId: string) => {
    const response = await axios.get(`/questions/public/${projectId}`);
    return response.data.questions;
  },

  getById: async (id: string) => {
    const response = await axios.get(`/questions/${id}`);
    return response.data.question;
  },

  create: async (questionData: any) => {
    const response = await axios.post('/questions', questionData);
    return response.data.question;
  },

  createPublic: async (questionData: any) => {
    const response = await axios.post('/questions/public', questionData);
    return response.data;
  },

  update: async (id: string, questionData: any) => {
    const response = await axios.put(`/questions/${id}`, questionData);
    return response.data.question;
  },

  answer: async (id: string, answerData: any) => {
    const response = await axios.post(`/questions/${id}/answer`, answerData);
    return response.data.question;
  },

  delete: async (id: string) => {
    await axios.delete(`/questions/${id}`);
  },

  vote: async (id: string, helpful: boolean) => {
    const response = await axios.post(`/questions/${id}/vote`, { helpful });
    return response.data.metrics;
  },

  exportCSV: async (projectId: string) => {
    const response = await axios.get(`/questions/project/${projectId}/export`, { responseType: 'blob' });
    return response.data;
  },

  suggestTags: async (projectId: string) => {
    const response = await axios.get(`/questions/project/${projectId}/suggest-tags`);
    return response.data.data;
  },
};

// Users API (Admin)
export const usersAPI = {
  getAll: async () => {
    const response = await axios.get('/users');
    return response.data.users;
  },

  getById: async (id: string) => {
    const response = await axios.get(`/users/${id}`);
    return response.data.user;
  },

  create: async (userData: any) => {
    const response = await axios.post('/users', userData);
    return response.data.user;
  },

  update: async (id: string, userData: any) => {
    const response = await axios.put(`/users/${id}`, userData);
    return response.data.user;
  },

  delete: async (id: string) => {
    await axios.delete(`/users/${id}`);
  },

  assignProjects: async (id: string, projectIds: string[]) => {
    const response = await axios.put(`/users/${id}/projects`, { projects: projectIds });
    return response.data.user;
  },

  uploadAvatar: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await axios.post(`/users/${id}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.avatar;
  },
};

// Companies API (Admin)
export const companiesAPI = {
  getAll: async () => {
    const response = await axios.get('/companies');
    return response.data.companies;
  },

  getById: async (id: string) => {
    const response = await axios.get(`/companies/${id}`);
    return response.data.company;
  },

  create: async (companyData: any) => {
    const response = await axios.post('/companies', companyData);
    return response.data.company;
  },

  update: async (id: string, companyData: any) => {
    const response = await axios.put(`/companies/${id}`, companyData);
    return response.data.company;
  },

  delete: async (id: string) => {
    await axios.delete(`/companies/${id}`);
  },

  uploadLogo: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await axios.post(`/companies/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteLogo: async (id: string) => {
    const response = await axios.delete(`/companies/${id}/logo`);
    return response.data;
  },

  getStats: async (id: string) => {
    const response = await axios.get(`/companies/${id}/stats`);
    return response.data.stats;
  },
};

// Admin API
export const adminAPI = {
  getStats: async () => {
    const response = await axios.get('/admin/stats');
    return response.data.stats;
  },

  getIntakes: async () => {
    const response = await axios.get('/admin/intakes');
    return response.data.intakes;
  },

  createIntake: async (intakeData: any) => {
    const response = await axios.post('/admin/intakes', intakeData);
    return response.data.intake;
  },

  updateIntake: async (id: string, intakeData: any) => {
    const response = await axios.put(`/admin/intakes/${id}`, intakeData);
    return response.data.intake;
  },

  deleteIntake: async (id: string) => {
    await axios.delete(`/admin/intakes/${id}`);
  },

  approveIntake: async (id: string) => {
    const response = await axios.post(`/admin/intakes/${id}/approve`);
    return response.data.project;
  },
};

// Relationships API
export const relationshipsAPI = {
  getByProject: async (projectId: string, params?: any) => {
    const response = await axios.get(`/relationships/project/${projectId}`, { params });
    return response.data;
  },

  getMatrix: async (projectId: string) => {
    const response = await axios.get(`/relationships/project/${projectId}/matrix`);
    return response.data.matrix;
  },

  getById: async (id: string) => {
    const response = await axios.get(`/relationships/${id}`);
    return response.data.relationship;
  },

  create: async (relationshipData: any) => {
    const response = await axios.post('/relationships', relationshipData);
    return response.data.relationship;
  },

  update: async (id: string, relationshipData: any) => {
    const response = await axios.put(`/relationships/${id}`, relationshipData);
    return response.data.relationship;
  },

  delete: async (id: string) => {
    await axios.delete(`/relationships/${id}`);
  },

  addInteraction: async (id: string, interactionData: any) => {
    const response = await axios.post(`/relationships/${id}/interaction`, interactionData);
    return response.data.relationship;
  },

  addCommitment: async (id: string, commitmentData: any) => {
    const response = await axios.post(`/relationships/${id}/commitment`, commitmentData);
    return response.data.relationship;
  },

  getAnalytics: async (projectId: string) => {
    const response = await axios.get(`/relationships/project/${projectId}/analytics`);
    return response.data.analytics;
  },

  bulkImport: async (projectId: string, items: any[]) => {
    const response = await axios.post(`/relationships/project/${projectId}/bulk-import`, { items });
    return response.data;
  },
};

// AI Assistant API
export const aiAssistantAPI = {
  chat: async (projectId: string, message: string, conversationHistory: Array<{ role: string; content: string }>) => {
    const response = await axios.post('/ai-assistant/chat', {
      projectId,
      message,
      conversationHistory,
    });
    return response.data;
  },

  getSuggestions: async (projectId: string) => {
    const response = await axios.get(`/ai-assistant/suggestions/${projectId}`);
    return response.data.suggestions;
  },
};

// AI Document API
export const aiDocumentAPI = {
  analyze: async (document: File) => {
    const formData = new FormData();
    formData.append('document', document);

    const response = await axios.post('/ai-document/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  preview: async (document: File) => {
    const formData = new FormData();
    formData.append('document', document);

    const response = await axios.post('/ai-document/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  importToProject: async (projectId: string, document: File) => {
    const formData = new FormData();
    formData.append('document', document);

    const response = await axios.post(`/ai-document/project/${projectId}/import-document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// Area Analysis API (Gebiedsanalyse)
export const analysisAPI = {
  createArea: async (projectId: string, data: { name?: string; boundary: any; influenceRadius?: number }) => {
    const response = await axios.post('/analysis/area', { projectId, ...data });
    return response.data.data;
  },

  getArea: async (projectId: string) => {
    const response = await axios.get(`/analysis/area/${projectId}`);
    return response.data.data;
  },

  updateArea: async (areaId: string, data: { boundary?: any; influenceRadius?: number; name?: string }) => {
    const response = await axios.put(`/analysis/area/${areaId}`, data);
    return response.data.data;
  },

  deleteArea: async (areaId: string) => {
    await axios.delete(`/analysis/area/${areaId}`);
  },

  geocode: async (query: string) => {
    const response = await axios.get('/analysis/geocode', { params: { q: query } });
    return response.data.data;
  },

  refreshData: async (areaId: string) => {
    const response = await axios.post(`/analysis/refresh/${areaId}`);
    return response.data.data;
  },

  getOmgevingsdata: async (projectId: string) => {
    const response = await axios.get(`/analysis/omgevingsdata/${projectId}`);
    return response.data.data;
  },

  generateProfiel: async (areaId: string, customInstruction?: string) => {
    const response = await axios.post(`/analysis/generate-profiel/${areaId}`, {
      customInstruction
    });
    return response.data.data;
  },

  saveMarkers: async (areaId: string, markers: any[]) => {
    const response = await axios.put(`/analysis/area/${areaId}/markers`, { markers });
    return response.data.data;
  },

  getInvloedsgebiedAnalyse: async (areaId: string, filters?: string[]) => {
    const params: Record<string, string> = {};
    if (filters && filters.length > 0) {
      params.filters = filters.join(',');
    }
    const response = await axios.get(`/analysis/invloedsgebied/${areaId}`, { params });
    return response.data.data;
  },

  getRaakvlakprojecten: async (areaId: string, radius?: number) => {
    const params: Record<string, string> = {};
    if (radius) params.radius = String(radius);
    const response = await axios.get(`/analysis/raakvlakprojecten/${areaId}`, { params });
    return response.data.data;
  },

  kvkSearch: async (params: { naam?: string; postcode?: string; plaats?: string }) => {
    const response = await axios.get('/analysis/kvk/search', { params });
    return response.data.data;
  },

  kvkEnrich: async (items: any[]) => {
    const response = await axios.post('/analysis/kvk/enrich', { items });
    return response.data.data;
  },
};

// Public Website Admin API (authenticated)
export const publicWebsiteAPI = {
  getSettings: async (projectId: string) => {
    const response = await axios.get(`/projects/${projectId}/public-website`);
    return response.data;
  },

  updateSettings: async (projectId: string, settings: any) => {
    const response = await axios.put(`/projects/${projectId}/public-website`, settings);
    return response.data.publicWebsite;
  },

  checkSlug: async (projectId: string, slug: string) => {
    const response = await axios.post(`/projects/${projectId}/public-website/check-slug`, { slug });
    return response.data.available;
  },

  getAnalytics: async (projectId: string) => {
    const response = await axios.get(`/projects/${projectId}/public-website/analytics`);
    return response.data;
  },

  getSubscribers: async (projectId: string) => {
    const response = await axios.get(`/projects/${projectId}/public-website/subscribers`);
    return response.data;
  },

  exportSubscribers: async (projectId: string) => {
    const response = await axios.get(`/projects/${projectId}/public-website/subscribers/export`, {
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'nieuwsbrief-abonnees.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};

// Public Website Visitor API (unauthenticated — separate axios instance)
const publicAxios = axios.create({
  baseURL: getApiBaseUrl(),
});

export const publicSiteAPI = {
  getBySlug: async (slug: string) => {
    const response = await publicAxios.get(`/public-site/${slug}`);
    return response.data.project;
  },

  getFaq: async (slug: string) => {
    const response = await publicAxios.get(`/public-site/${slug}/faq`);
    return response.data.questions;
  },

  getTimeline: async (slug: string) => {
    const response = await publicAxios.get(`/public-site/${slug}/timeline`);
    return response.data;
  },

  getMedia: async (slug: string) => {
    const response = await publicAxios.get(`/public-site/${slug}/media`);
    return response.data.media;
  },

  getGebiedsanalyse: async (slug: string) => {
    const response = await publicAxios.get(`/public-site/${slug}/gebiedsanalyse`);
    return response.data.data;
  },

  getOmgevingsdata: async (slug: string) => {
    const response = await publicAxios.get(`/public-site/${slug}/omgevingsdata`);
    return response.data.data;
  },

  submitContact: async (slug: string, data: { name: string; email: string; subject?: string; message: string }) => {
    const response = await publicAxios.post(`/public-site/${slug}/contact`, data);
    return response.data;
  },

  subscribeNewsletter: async (slug: string, email: string) => {
    const response = await publicAxios.post(`/public-site/${slug}/newsletter`, { email });
    return response.data;
  },
};

// Issue Radar API
export const issueRadarAPI = {
  getOverview: async (projectId: string) => {
    const response = await axios.get(`/issue-radar/project/${projectId}/overview`);
    return response.data.overview;
  },

  getIssues: async (projectId: string, params?: any) => {
    const response = await axios.get(`/issue-radar/project/${projectId}/issues`, { params });
    return response.data.issues;
  },

  getIssueDetail: async (projectId: string, issueId: string) => {
    const response = await axios.get(`/issue-radar/project/${projectId}/issues/${issueId}`);
    return response.data;
  },

  updateIssue: async (projectId: string, issueId: string, data: any) => {
    const response = await axios.put(`/issue-radar/project/${projectId}/issues/${issueId}`, data);
    return response.data.issue;
  },

  getSignals: async (projectId: string, params?: any) => {
    const response = await axios.get(`/issue-radar/project/${projectId}/signals`, { params });
    return response.data;
  },

  getSignalStats: async (projectId: string) => {
    const response = await axios.get(`/issue-radar/project/${projectId}/signals/stats`);
    return response.data.stats;
  },

  sync: async (projectId: string) => {
    const response = await axios.post(`/issue-radar/project/${projectId}/sync`);
    return response.data.result;
  },

  detect: async (projectId: string) => {
    const response = await axios.post(`/issue-radar/project/${projectId}/detect`);
    return response.data;
  },
};

// Participation API
export const participationAPI = {
  getByProject: async (projectId: string, params?: any) => {
    const response = await axios.get(`/participation/project/${projectId}`, { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await axios.get(`/participation/${id}`);
    return response.data.participation;
  },
  create: async (data: any) => {
    const response = await axios.post('/participation', data);
    return response.data.participation;
  },
  update: async (id: string, data: any) => {
    const response = await axios.put(`/participation/${id}`, data);
    return response.data.participation;
  },
  delete: async (id: string) => {
    await axios.delete(`/participation/${id}`);
  },
  submitResponse: async (id: string, data: any) => {
    const response = await axios.post(`/participation/${id}/respond`, data);
    return response.data;
  },
  getResults: async (id: string) => {
    const response = await axios.get(`/participation/${id}/results`);
    return response.data.results;
  },
  activate: async (id: string) => {
    const response = await axios.post(`/participation/${id}/activate`);
    return response.data.participation;
  },
  close: async (id: string) => {
    const response = await axios.post(`/participation/${id}/close`);
    return response.data.participation;
  },
};

// Reports API
export const reportsAPI = {
  getByProject: async (projectId: string, params?: any) => {
    const response = await axios.get(`/reports/project/${projectId}`, { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await axios.get(`/reports/${id}`);
    return response.data.report;
  },
  generate: async (data: any) => {
    const response = await axios.post('/reports/generate', data);
    return response.data.report;
  },
  update: async (id: string, data: any) => {
    const response = await axios.put(`/reports/${id}`, data);
    return response.data.report;
  },
  delete: async (id: string) => {
    await axios.delete(`/reports/${id}`);
  },
  getAvailable: async (projectId: string) => {
    const response = await axios.get(`/reports/project/${projectId}/available`);
    return response.data.availableTypes;
  },
};

// Permits API
export const permitsAPI = {
  getByProject: async (projectId: string, params?: any) => {
    const response = await axios.get(`/permits/project/${projectId}`, { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await axios.get(`/permits/${id}`);
    return response.data.permit;
  },
  create: async (data: any) => {
    const response = await axios.post('/permits', data);
    return response.data.permit;
  },
  update: async (id: string, data: any) => {
    const response = await axios.put(`/permits/${id}`, data);
    return response.data.permit;
  },
  delete: async (id: string) => {
    await axios.delete(`/permits/${id}`);
  },
  addProcedure: async (id: string, data: any) => {
    const response = await axios.post(`/permits/${id}/procedure`, data);
    return response.data.permit;
  },
  addObjection: async (id: string, data: any) => {
    const response = await axios.post(`/permits/${id}/objection`, data);
    return response.data.permit;
  },
  getTimeline: async (projectId: string) => {
    const response = await axios.get(`/permits/project/${projectId}/timeline`);
    return response.data;
  },
};

// Knowledge Base API
export const knowledgeBaseAPI = {
  getAll: async (params?: any) => {
    const response = await axios.get('/knowledge-base', { params });
    return response.data;
  },
  getById: async (id: string) => {
    const response = await axios.get(`/knowledge-base/${id}`);
    return response.data.article;
  },
  create: async (data: any) => {
    const response = await axios.post('/knowledge-base', data);
    return response.data.article;
  },
  update: async (id: string, data: any) => {
    const response = await axios.put(`/knowledge-base/${id}`, data);
    return response.data.article;
  },
  delete: async (id: string) => {
    await axios.delete(`/knowledge-base/${id}`);
  },
  rate: async (id: string, score: number, comment?: string) => {
    const response = await axios.post(`/knowledge-base/${id}/rate`, { score, comment });
    return response.data.article;
  },
  trackUse: async (id: string) => {
    await axios.post(`/knowledge-base/${id}/use`);
  },
  getCategories: async () => {
    const response = await axios.get('/knowledge-base/meta/categories');
    return response.data;
  },
};

// Stakeholder Network API
export const stakeholderNetworkAPI = {
  getNetwork: async (projectId: string) => {
    const response = await axios.get(`/stakeholder-network/project/${projectId}`);
    return response.data;
  },
};

export default {
  auth: authAPI,
  projects: projectsAPI,
  dashboard: dashboardAPI,
  sentiment: sentimentAPI,
  personas: personasAPI,
  strategy: strategyAPI,
  media: mediaAPI,
  workPlanning: workPlanningAPI,
  questions: questionsAPI,
  relationships: relationshipsAPI,
  users: usersAPI,
  companies: companiesAPI,
  admin: adminAPI,
  aiAssistant: aiAssistantAPI,
  aiDocument: aiDocumentAPI,
  publicWebsite: publicWebsiteAPI,
  publicSite: publicSiteAPI,
  analysis: analysisAPI,
  issueRadar: issueRadarAPI,
  participation: participationAPI,
  reports: reportsAPI,
  permits: permitsAPI,
  knowledgeBase: knowledgeBaseAPI,
  stakeholderNetwork: stakeholderNetworkAPI,
};